import { Injectable } from '@angular/core';

export const BANNER_DISMISSAL_KEY_PREFIX: string = 'bannerDismissedUntil';

/** How long a dismissed banner stays hidden before it reappears. */
export const BANNER_DISMISSAL_DURATION_MS: number = 24 * 60 * 60 * 1000;

/** Banners that offer a dismiss control. Add new ids here rather than passing free strings. */
export type DismissibleBanner = 'stripeActivation' | 'pwaInstall';

/**
 * Owns localStorage persistence of temporarily dismissed alert banners.
 *
 * A dismissal stores an expiry timestamp, so the banner comes back on its own
 * once the window lapses — it is a snooze, not a permanent opt-out. Keys are
 * scoped by the entity the banner is about (e.g. charity id) so dismissing it
 * for one charity doesn't hide it for another on the same device.
 *
 * All reads and writes are wrapped in try/catch — storage may be unavailable
 * (restricted contexts, private browsing quota, test stubs). A failed write is
 * a silent no-op; a failed read reports "not dismissed", so the banner shows.
 */
@Injectable({ providedIn: 'root' })
export class BannerDismissalService {
  dismiss(
    banner: DismissibleBanner,
    scopeId: string,
    durationMs: number = BANNER_DISMISSAL_DURATION_MS,
  ): void {
    const key: string | null = this.buildKey(banner, scopeId);
    if (!key) return;
    try {
      localStorage.setItem(key, String(Date.now() + durationMs));
    } catch {
      // Storage unavailable or quota exceeded — persistence is best-effort
    }
  }

  isDismissed(banner: DismissibleBanner, scopeId: string): boolean {
    const key: string | null = this.buildKey(banner, scopeId);
    if (!key) return false;
    try {
      const raw: string | null = localStorage.getItem(key);
      if (!raw) return false;
      const expiresAt: number = Number(raw);
      if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
        localStorage.removeItem(key);
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  private buildKey(banner: DismissibleBanner, scopeId: string): string | null {
    if (!scopeId) return null;
    return `${BANNER_DISMISSAL_KEY_PREFIX}:${banner}:${scopeId}`;
  }
}
